import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { booleanPointInPolygon, point } from '@turf/turf';
import { useTheme } from 'styled-components';

import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { getPolygonDrawColors } from './polygon.helpers';

import type { Feature, Geometry, MultiPolygon, Polygon } from 'geojson';
import type { MapMouseEvent } from 'mapbox-gl';

type Props = {
  mapId: string;
};

export function PolygonEditControl({ mapId }: Props) {
  const { [mapId]: map } = useMap();
  const drawRef = useRef<MapboxDraw | null>(null);
  const { editedPolygonId, mode, setMode, shapes, updatePolygon } = useMultiFlightMap();
  const { uiColors }: any = useTheme();
  const previousEditedPolygonIdRef = useRef<string | null>(null);

  const cleanupDraw = () => {
    if (drawRef.current && map?.getMap()) {
      try {
        drawRef.current.changeMode('simple_select', { featureIds: [] });
        drawRef.current.deleteAll();
        map.getMap().removeControl(drawRef.current);
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: off
        console.warn('Error removing draw control:', e);
      } finally {
        drawRef.current = null;
      }
    }
  };

  const closeRing = (coords: [number, number][]): [number, number][] => {
    if (coords.length < 3) {
      return coords;
    }
    const first = coords[0];
    const last = coords[coords.length - 1];

    const isClosed = first[0] === last[0] && first[1] === last[1];
    return isClosed ? coords : [...coords, first];
  };

  const saveChanges = () => {
    if (!drawRef.current) {
      return;
    }

    const data = drawRef.current.getAll();
    const updated = data.features[0];

    if (updated?.geometry?.type === 'Polygon') {
      const rawCoords = updated.geometry.coordinates[0] as [number, number][];
      const closed = closeRing(rawCoords);
      const shape = shapes.find(z => z.id === editedPolygonId);
      if (shape) {
        updatePolygon(shape.id, closed);
      }
      cleanupDraw();
      if (editedPolygonId === previousEditedPolygonIdRef.current) {
        setMode(null);
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      cleanupDraw();
      setMode(null);
    }
    if (e.key === 'Enter') {
      saveChanges();
    }
  };

  const handleMapClick = (e: MapMouseEvent) => {
    if (!drawRef.current) {
      return;
    }
    const drawFeatures = drawRef.current.getAll().features;
    const clickedOnDrawFeature = drawFeatures.some(f => {
      if (!f || !f.geometry) {
        return false;
      }
      const isPolygonLike = (geom: Geometry): geom is Polygon | MultiPolygon =>
        geom.type === 'Polygon' || geom.type === 'MultiPolygon';
      if (!isPolygonLike(f.geometry)) {
        return false;
      }
      const polygonFeature: Feature<Polygon | MultiPolygon> = {
        geometry: f.geometry,
        properties: f.properties || {},
        type: 'Feature'
      };
      return booleanPointInPolygon(point([e.lngLat.lng, e.lngLat.lat]), polygonFeature);
    });

    if (!clickedOnDrawFeature) {
      saveChanges();
    }
  };

  const handleDocumentClick = (event: MouseEvent) => {
    const toolbarEl = document.querySelector('.toolbarMap');
    if (toolbarEl && toolbarEl.contains(event.target as Node)) {
      handleMapClick({
        lngLat: {
          lat: 0,
          lng: 0
        },
        originalEvent: event,
        target: map?.getMap(),
        type: 'click'
      } as unknown as MapMouseEvent);
    }
  };

  useEffect(() => {
    const mapInstance = map?.getMap();

    if (
      previousEditedPolygonIdRef.current &&
      previousEditedPolygonIdRef.current !== editedPolygonId &&
      mode === 'edit-polygon'
    ) {
      saveChanges();
    }

    if (!mapInstance || mode !== 'edit-polygon') {
      cleanupDraw();
      previousEditedPolygonIdRef.current = editedPolygonId;
      return;
    }

    const draw = new MapboxDraw({
      controls: {
        polygon: false
      },
      displayControlsDefault: false,
      styles: getPolygonDrawColors(uiColors),
      userProperties: true
    });

    if (drawRef.current) {
      cleanupDraw();
    }

    drawRef.current = draw;
    mapInstance.addControl(draw);

    if (mode === 'edit-polygon' && editedPolygonId) {
      const shape = shapes.find(s => s.id === editedPolygonId);
      if (shape && shape.type === 'Polygon') {
        const feature: Feature = {
          geometry: {
            coordinates: shape.coordinates,
            type: 'Polygon'
          },
          id: shape.id,
          properties: {},
          type: 'Feature'
        };

        draw.add(feature);
        draw.changeMode('direct_select', { featureId: shape.id });
      } else {
        cleanupDraw();
        setMode(null);
      }
    }

    mapInstance.on('click', handleMapClick);
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleDocumentClick);

    previousEditedPolygonIdRef.current = editedPolygonId;

    return () => {
      mapInstance.off('click', handleMapClick);
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleDocumentClick);
      cleanupDraw();
    };
  }, [editedPolygonId, map, mode]);

  return null;
}
